import React from 'react';
import get from 'lodash/get';
import {
  Action,
  withInject,
} from '@piwikpro/platform';
import {
  Stack,
  Tooltip,
  Icon,
  withTranslation,
} from '@piwikpro/ui-components';
import { TrackerSettings, TrackerSettingsResponse } from '../../../TrackerSettings';

export interface ActionOnSessionLimitExceededProps {
  appId: string
  trackerSettings: TrackerSettings
  appTrackerSettings: TrackerSettingsResponse
  globalTrackerSettings: TrackerSettingsResponse
  t: (key: string) => string
}

const SPLIT_AND_EXCLUDE = 'split_and_exclude';
const JUST_SPLIT = 'just_split';
const defaultValue = SPLIT_AND_EXCLUDE;

class ActionOnSessionLimitExceeded extends React.Component<
ActionOnSessionLimitExceededProps, {
  useGlobals: boolean
}> {
  static readonly fieldName = 'session_limit_exceeded_action';

  static readonly globalFieldName = 'session_limit_exceeded_action_global';

  constructor(props: ActionOnSessionLimitExceededProps) {
    super(props);
    const { appTrackerSettings } = props;

    this.state = {
      useGlobals: appTrackerSettings.attributes?.[
        ActionOnSessionLimitExceeded.fieldName
      ] === undefined,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSubmitCheckbox = this.handleSubmitCheckbox.bind(this);
  }

  async handleSubmit(values, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }) {
    const {
      trackerSettings,
      appId,
    } = this.props;
    const field = Object.keys(values)[0];

    try {
      await trackerSettings.updateSettingsForApp(appId, {
        [ActionOnSessionLimitExceeded.fieldName]: values[ActionOnSessionLimitExceeded.fieldName],
      });
    } catch (err: any) {
      setErrors(err.errors);
      setFieldValue(
        field,
        this.getValue(),
        false,
      );
    }

    setSubmitting(false);
  }

  async handleSubmitCheckbox(values, { setFieldValue, setSubmitting }) {
    const {
      trackerSettings,
      appId,
    } = this.props;

    this.setState({
      useGlobals: values[ActionOnSessionLimitExceeded.globalFieldName],
    });

    try {
      if (values[ActionOnSessionLimitExceeded.globalFieldName]) {
        await trackerSettings.deleteSettingsForApp(
          appId,
          ActionOnSessionLimitExceeded.fieldName,
        );
      } else {
        await trackerSettings.updateSettingsForApp(appId, {
          [ActionOnSessionLimitExceeded.fieldName]: this.getGlobalValue(),
        });
      }
    } catch (err) {
      setFieldValue(
        ActionOnSessionLimitExceeded.globalFieldName,
        !values[ActionOnSessionLimitExceeded.globalFieldName],
        false,
      );
      this.setState({
        useGlobals: !values[ActionOnSessionLimitExceeded.globalFieldName],
      });
    }

    setSubmitting(false);
  }

  getGlobalValue() {
    const { globalTrackerSettings } = this.props;

    return get(
      globalTrackerSettings.attributes,
      ActionOnSessionLimitExceeded.fieldName,
      defaultValue,
    );
  }

  getValue() {
    const { appTrackerSettings } = this.props;
    const { useGlobals } = this.state;

    const globalValue = this.getGlobalValue();
    const applicationValue = get(
      appTrackerSettings.attributes,
      ActionOnSessionLimitExceeded.fieldName,
      globalValue,
    );

    return useGlobals ? globalValue : applicationValue;
  }

  render() {
    const { t } = this.props;
    const { useGlobals } = this.state;

    const value = this.getValue();

    return (
      <Stack vertical>
        <Stack.Item fill>
          <Stack vertical spacing="narrow" horizontalAlignment="fill">
            <Stack.Item>
              <Action.AutoSavedSelectControl
                id="app-settings-action-on-session-limit-exceeded"
                label={t('apps.details.settings.session-limits.action-on-session-limit-exceeded.title')}
                name={ActionOnSessionLimitExceeded.fieldName}
                initialValue={value}
                disabled={useGlobals}
                options={[{
                  id: 'app-settings-action-on-session-limit-exceeded-value',
                  items: [
                    {
                      id: SPLIT_AND_EXCLUDE,
                      value: SPLIT_AND_EXCLUDE,
                      name: t('apps.details.settings.session-limits.action-on-session-limit-exceeded.options.split-and-exclude'),
                    },
                    {
                      id: JUST_SPLIT,
                      value: JUST_SPLIT,
                      name: t('apps.details.settings.session-limits.action-on-session-limit-exceeded.options.just-split'),
                    },
                  ],
                }]}
                onSubmit={this.handleSubmit}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack spacing="narrow">
            <Stack.Item>
              <Action.AutoSavedCheckboxControl
                id="app-settings-action-on-session-limit-exceeded-use-globals"
                onSubmit={this.handleSubmitCheckbox}
                initialValue={useGlobals}
                label={t('app.details.settings.general.use-global-settings.label')}
                name={ActionOnSessionLimitExceeded.globalFieldName}
              />
            </Stack.Item>
            <Stack.Item>
              <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                <div>
                  <Icon name="info-circle" />
                </div>
              </Tooltip>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(
  withTranslation(['administration'])(ActionOnSessionLimitExceeded as any),
);
